'use client';

import { useState } from 'react';
import {
    FileText,
    Download,
    FileDown,
    Search,
    Filter,
    Calendar,
    ChevronDown,
    Eye,
    Lock,
    Printer,
    History,
    ArrowUpDown,
    CheckCircle2,
    X,
    Plus
} from 'lucide-react';
import Link from 'next/link';

// --- Types ---

type JournalType = 'Otomatis' | 'Manual';
type JournalSource = 'Setoran Modal' | 'Setoran Masuk' | 'Pengeluaran Biaya' | 'Transfer Kas' | 'Jurnal Manual';

interface JournalEntry {
    accountCode: string;
    accountName: string;
    debit: number;
    credit: number;
}

interface JournalHeader {
    id: string;
    date: string;
    description: string;
    type: JournalType;
    source: JournalSource;
    referenceId?: string;
    entries: JournalEntry[];
    isLocked: boolean;
}

// --- Mock Data ---

const MOCK_JOURNALS: JournalHeader[] = [
    {
        id: 'JRN-2024-001',
        date: '2024-02-10',
        description: 'Pembelian Kertas A4 & Tinta Printer',
        type: 'Otomatis',
        source: 'Pengeluaran Biaya',
        referenceId: 'EXP-2024-001',
        isLocked: true,
        entries: [
            { accountCode: '5-101', accountName: 'Beban ATK', debit: 450000, credit: 0 },
            { accountCode: '1-100', accountName: 'Kas Tunai', debit: 0, credit: 450000 }
        ]
    },
    {
        id: 'JRN-2024-002',
        date: '2024-02-09',
        description: 'Penerimaan SPP - Budi Santoso',
        type: 'Otomatis',
        source: 'Setoran Masuk',
        referenceId: 'STM-2024-001',
        isLocked: false,
        entries: [
            { accountCode: '1-101', accountName: 'Bank BSI', debit: 500000, credit: 0 },
            { accountCode: '4-101', accountName: 'Pendapatan SPP', debit: 0, credit: 500000 }
        ]
    },
    {
        id: 'JRN-2024-003',
        date: '2024-02-08',
        description: 'Setoran Modal Yayasan - Tahap 1',
        type: 'Otomatis',
        source: 'Setoran Modal',
        referenceId: 'CPD-2024-001',
        isLocked: false,
        entries: [
            { accountCode: '1-102', accountName: 'Bank Mandiri', debit: 50000000, credit: 0 },
            { accountCode: '3-101', accountName: 'Modal Yayasan', debit: 0, credit: 50000000 }
        ]
    },
    {
        id: 'JRN-2024-004',
        date: '2024-02-07',
        description: 'Koreksi Saldo Awal Kas Laci',
        type: 'Manual',
        source: 'Jurnal Manual',
        isLocked: false,
        entries: [
            { accountCode: '1-100', accountName: 'Kas Tunai', debit: 50000, credit: 0 },
            { accountCode: '4-200', accountName: 'Pendapatan Lain-lain', debit: 0, credit: 50000 }
        ]
    }
];

export default function LaporanJurnalPage() {
    // --- State ---
    const [journals] = useState<JournalHeader[]>(MOCK_JOURNALS);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [sourceFilter, setSourceFilter] = useState<string>('all');
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedJournal, setSelectedJournal] = useState<JournalHeader | null>(null);

    // --- Helpers ---
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const handleViewDetail = (journal: JournalHeader) => {
        setSelectedJournal(journal);
        setIsDetailModalOpen(true);
    };

    // --- Filter logic ---
    const filteredJournals = journals.filter(j => {
        const matchesSearch = j.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            j.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = typeFilter === 'all' || j.type === typeFilter;
        const matchesSource = sourceFilter === 'all' || j.source === sourceFilter;
        return matchesSearch && matchesType && matchesSource;
    });

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Header */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                            <Link href="/sekolah" className="hover:text-primary transition-all">Laporan</Link>
                            <span className="text-muted/50">•</span>
                            <span className="text-foreground">Jurnal</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-primary-soft border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                <FileText className="w-6 h-6" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-black text-foreground tracking-tight">Laporan Jurnal</h1>
                                <p className="text-xs font-medium text-secondary">PAUD Al-Azhar 1 • Tahun Ajaran 2024/2025</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        <button className="flex items-center gap-2 px-5 py-3 bg-background border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-primary/30 transition-all shadow-sm">
                            <FileDown className="w-4 h-4 text-primary" /> Export Excel
                        </button>
                        <button className="flex items-center gap-2 px-5 py-3 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all">
                            <Printer className="w-4 h-4" /> Export PDF
                        </button>
                    </div>
                </div>
            </section>

            {/* Filters */}
            <div className="bg-surface border border-border-main p-4 rounded-3xl shadow-sm space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1 group w-full md:max-w-xs transition-all">
                        <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted group-focus-within:text-primary transition-colors" />
                        <input
                            type="text"
                            placeholder="Cari No. Jurnal / Keterangan..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-background/50 border border-border-main rounded-2xl py-3.5 pl-12 pr-6 text-xs font-semibold focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all outline-none"
                        />
                    </div>

                    <div className="flex flex-wrap gap-3 flex-1">
                        <div className="relative group min-w-[140px]">
                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                className="w-full pl-4 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-bold uppercase tracking-widest outline-none appearance-none cursor-pointer hover:border-primary/30 transition-all text-muted"
                            >
                                <option value="all">Jenis Jurnal</option>
                                <option value="Otomatis">Otomatis</option>
                                <option value="Manual">Manual</option>
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted pointer-events-none" />
                        </div>

                        <div className="relative group min-w-[150px]">
                            <select
                                value={sourceFilter}
                                onChange={(e) => setSourceFilter(e.target.value)}
                                className="w-full pl-4 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-bold uppercase tracking-widest outline-none appearance-none cursor-pointer hover:border-primary/30 transition-all text-muted"
                            >
                                <option value="all">Sumber Transaksi</option>
                                <option value="Setoran Modal">Setoran Modal</option>
                                <option value="Setoran Masuk">Setoran Masuk</option>
                                <option value="Pengeluaran Biaya">Pengeluaran</option>
                                <option value="Transfer Kas">Transfer</option>
                                <option value="Jurnal Manual">Jurnal Manual</option>
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted pointer-events-none" />
                        </div>

                        <button className="flex items-center gap-3 px-5 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-bold uppercase tracking-widest text-muted hover:border-primary/30 transition-all shadow-sm">
                            <Calendar className="w-4 h-4" /> 01 Feb 2024 - 29 Feb 2024
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-surface border border-border-main rounded-3xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[140px]">Tanggal</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[140px]">No. Jurnal</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[250px]">Keterangan / Akun</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[150px] text-right">Debit</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[150px] text-right">Kredit</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-center">Status</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main/50">
                            {filteredJournals.length > 0 ? filteredJournals.map((j) => (
                                <React.Fragment key={j.id}>
                                    {/* Main Row / Group Header */}
                                    <tr className="bg-primary-soft/5">
                                        <td className="px-6 py-4">
                                            <span className="text-[11px] font-black text-foreground">{j.date}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-[11px] font-black text-primary hover:underline cursor-pointer">{j.id}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="space-y-1">
                                                <p className="text-[11px] font-bold text-foreground line-clamp-1">{j.description}</p>
                                                <div className="flex items-center gap-2">
                                                    <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-wider border ${j.type === 'Otomatis' ? 'bg-success-soft text-success border-success/10' : 'bg-warning-soft text-warning border-warning/10'}`}>
                                                        {j.type}
                                                    </span>
                                                    <span className="text-[9px] font-bold text-muted uppercase tracking-tighter">Ref: {j.referenceId || '-'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4"></td>
                                        <td className="px-6 py-4"></td>
                                        <td className="px-6 py-4 text-center">
                                            {j.isLocked && <Lock className="w-3.5 h-3.5 text-muted mx-auto" title="Periode Terkunci" />}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <button
                                                onClick={() => handleViewDetail(j)}
                                                className="p-2 rounded-xl text-muted hover:text-primary hover:bg-primary-soft transition-all"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                    {/* Entry Rows */}
                                    {j.entries.map((entry, idx) => (
                                        <tr key={`${j.id}-${idx}`} className="group hover:bg-background transition-colors">
                                            <td colSpan={2}></td>
                                            <td className="px-8 py-3">
                                                <div className={`flex items-center gap-3 ${entry.credit > 0 ? 'ml-6 border-l-2 border-primary-soft pl-4' : ''}`}>
                                                    <span className="text-[10px] font-bold text-muted font-mono">{entry.accountCode}</span>
                                                    <span className="text-[11px] font-medium text-secondary">{entry.accountName}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-3 text-right">
                                                <span className="text-[11px] font-black text-foreground tabular-nums">{entry.debit > 0 ? formatCurrency(entry.debit) : '-'}</span>
                                            </td>
                                            <td className="px-6 py-3 text-right">
                                                <span className="text-[11px] font-black text-foreground tabular-nums">{entry.credit > 0 ? formatCurrency(entry.credit) : '-'}</span>
                                            </td>
                                            <td colSpan={2}></td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            )) : (
                                <tr>
                                    <td colSpan={7} className="px-6 py-10 text-center text-muted font-medium italic">
                                        Tidak ada data jurnal ditemukan
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detail Modal */}
            {isDetailModalOpen && selectedJournal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-surface rounded-4xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-300">
                        <div className="p-8 border-b border-border-main flex items-center justify-between bg-primary-soft/5">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-white rounded-2xl border border-primary/10 flex items-center justify-center text-primary shadow-sm">
                                    <History className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-foreground">Detail Entri Jurnal</h3>
                                    <p className="text-[10px] font-black text-primary uppercase tracking-widest">{selectedJournal.id}</p>
                                </div>
                            </div>
                            <button onClick={() => setIsDetailModalOpen(false)} className="p-2 rounded-full hover:bg-secondary-soft/50 text-muted transition-all"><X className="w-5 h-5" /></button>
                        </div>

                        <div className="p-8 overflow-y-auto space-y-8 flex-1 custom-scrollbar">
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-widest">Tanggal Jurnal</label>
                                    <p className="text-sm font-bold text-foreground">{selectedJournal.date}</p>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-widest">Jenis & Sumber</label>
                                    <p className="text-sm font-bold text-foreground uppercase tracking-tighter">{selectedJournal.type} • {selectedJournal.source}</p>
                                </div>
                                <div className="col-span-2 space-y-1">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-widest">Keterangan Jurnal</label>
                                    <p className="text-sm font-medium text-secondary bg-background/50 p-4 rounded-2xl border border-border-main leading-relaxed">
                                        {selectedJournal.description}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <label className="text-[10px] font-black text-muted uppercase tracking-widest">Breakdown Akun</label>
                                <div className="border border-border-main rounded-3xl overflow-hidden shadow-sm">
                                    <table className="w-full text-left text-xs">
                                        <thead>
                                            <tr className="bg-background border-b border-border-main">
                                                <th className="px-4 py-3 font-black text-muted uppercase tracking-widest text-[9px]">Akun</th>
                                                <th className="px-4 py-3 font-black text-muted uppercase tracking-widest text-[9px] text-right">Debit</th>
                                                <th className="px-4 py-3 font-black text-muted uppercase tracking-widest text-[9px] text-right">Kredit</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border-main/50 font-medium">
                                            {selectedJournal.entries.map((e, idx) => (
                                                <tr key={idx} className="bg-surface/50">
                                                    <td className="px-4 py-4">
                                                        <div className={`flex flex-col ${e.credit > 0 ? 'ml-6' : ''}`}>
                                                            <span className="font-black text-foreground">{e.accountName}</span>
                                                            <span className="text-[10px] text-muted font-mono">{e.accountCode}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4 text-right tabular-nums text-foreground">
                                                        {e.debit > 0 ? formatCurrency(e.debit) : '-'}
                                                    </td>
                                                    <td className="px-4 py-4 text-right tabular-nums text-foreground">
                                                        {e.credit > 0 ? formatCurrency(e.credit) : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="bg-primary/5 font-black">
                                                <td className="px-4 py-4 text-primary uppercase tracking-widest text-[10px]">Total</td>
                                                <td className="px-4 py-4 text-right text-primary tabular-nums">
                                                    {formatCurrency(selectedJournal.entries.reduce((a, b) => a + b.debit, 0))}
                                                </td>
                                                <td className="px-4 py-4 text-right text-primary tabular-nums">
                                                    {formatCurrency(selectedJournal.entries.reduce((a, b) => a + b.credit, 0))}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                <div className="flex items-center justify-center gap-2 p-3 bg-success-soft/30 rounded-2xl border border-success/10">
                                    <CheckCircle2 className="w-4 h-4 text-success" />
                                    <span className="text-[10px] font-black text-success uppercase tracking-widest">Jurnal Seimbarang / Balanced</span>
                                </div>
                            </div>
                        </div>

                        <div className="p-8 bg-background/50 border-t border-border-main flex gap-4">
                            <button onClick={() => setIsDetailModalOpen(false)} className="px-8 py-4 bg-white border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-secondary-soft/50 transition-all flex-1 shadow-sm">Tutup</button>
                            <button className="px-8 py-4 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all flex-1 flex items-center justify-center gap-2">
                                <Printer className="w-4 h-4" /> Cetak Bukti Jurnal
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}

import React from 'react';
